/**
 * Copy-workflow launcherResult helpers — pure data, no React, no I/O.
 *
 * Two halves, kept in ONE module because they must agree field-for-field:
 *   1. buildCopyReviewRows — launcherResult → the row shape CLW's ReviewGrid
 *      consumes (reusing CLW's own builders wherever the copy template's
 *      shape allows, with copy-specific overlays where it doesn't).
 *   2. applyCopyReviewEdits — grid edits ({rowId: {field: value}}) written
 *      back onto a deep clone of the launcherResult, ready to PUT to
 *      /planning-review. Every field copyGridConstants.js marks editable has
 *      a writer here; a field without a writer must never be marked editable.
 *
 * Why not just reuse CampaignLaunchWorkflow/reviewEdits.js wholesale: the
 * copy launchers emit templates whose key sets differ from the
 * parsed_response.json CLW reviews —
 *   - copy_ttd_campaign_launcher.py spells AdGroupId/AdGroupName/BaseBidCPM
 *     (capital A, bare-float money) where CLW's reader/writer expect
 *     adGroupId/adGroupName/BaseBidCPMInAdvertiserCurrency, so CLW's TTD
 *     applier resolves ZERO rows against a copy template and its row builder
 *     renders '—' names with no parent campaign;
 *   - copy_dv360_campaign_launcher.py maps by SELECT-and-rebuild against the
 *     DV360 launch validator's strict per-level key ALLOWLIST (unknown keys
 *     are hard validation errors) and carries NO targeting at all — the
 *     source's targeting rides launcherResult.targeting_plan, applied
 *     post-create — so CLW's targeting / package-rate / asset-clickUrl
 *     writers have nothing on a copy template to write to
 *     (copyGridConstants.js's COPY_DV360_DENYLIST names each one);
 *   - the Google Ads templates ARE the shape gads/gadsRowBuilders +
 *     gads/gadsEdits were built for, so those are reused directly, extended
 *     only with the copy grid's additional editable fields (campaign name,
 *     headline/description lists, click URL, PMax asset-group name).
 *
 * List cells (headlines/descriptions) are edited as ' · '-joined text — the
 * same separator the row builders join them with — and split back on commit.
 * A headline that itself contains ' · ' would be split; Google Ads text
 * assets don't use that sequence in practice, and the trade buys a working
 * editor inside CellDetailPanel's single-input contract.
 */

import { buildDv360ReviewRows } from '../CampaignLaunchWorkflow/dv360/dv360RowBuilders';
import { buildTtdReviewRows } from '../CampaignLaunchWorkflow/ttd/ttdRowBuilders';
import {
  buildGadsReviewRows, gadsResultSubtype,
} from '../CampaignLaunchWorkflow/gads/gadsRowBuilders';
// Google Ads writers live in ONE module (gads/gadsEdits.js) so this grid and
// the CLW review grid can't drift — both render Google Ads cells through
// gadsRowBuilders, so both must write the same underlying keys.
import {
  applyGadsEdits,
  applyGadsCampaignFields,
  applyGadsDisplayPath,
  parseGadsRowId,
  rebuildGadsKeywords,
  writeGadsList,
} from '../CampaignLaunchWorkflow/gads/gadsEdits';
import { isGadsPlatform } from '../CampaignLaunchWorkflow/gads/gadsConstants';
import { applyDv360RowOps, applyTtdRowOps } from '../CampaignLaunchWorkflow/rowOps';
// Field writers shared with CLW's reviewEdits.js (one implementation so the
// two appliers can't drift — both grids read cells through the same row
// builders, so both must write the same underlying keys).
import {
  numeric,
  scaleToParent,
  normalizeRowOps,
  ymdToTtdIso,
  writeTtdMoney,
  ttdPacingModeFromDraft,
  writeTtdKpiTarget,
  applyTtdFreqCap,
  applyTtdKpiType,
  applyDv360Pacing,
  applyDv360FreqCap,
  writeDv360DailyCap,
  writeDv360BidMicros,
  applyDv360BidStrategy,
  applyDv360BidGoal,
  applyDv360Expansion,
  applyDv360Pixel,
  applyDv360ConvWindow,
  applyDv360IoKpiType,
  applyDv360FlightDates,
  // The shared decoder for lookup-backed list values. Using it here is what keeps
  // both ends of the same wire format in agreement — see splitLookupValue.
  decodeLookupValue,
} from '../CampaignLaunchWorkflow/sharedFieldWriters';

const asArray = (v) => (Array.isArray(v) ? v : []);

/**
 * Which per-platform family a copy launcherResult belongs to.
 * 'dv360' | 'ttd' | 'gads' | null (null = not recognizable; render nothing
 * rather than guessing).
 */
export function copyResultFamily(launcherResult) {
  if (!launcherResult || typeof launcherResult !== 'object') return null;
  const platform = String(launcherResult.platform || launcherResult.Platform || '').toLowerCase();
  if (platform === 'ttd') return 'ttd';
  if (platform === 'dv360') return 'dv360';
  if (isGadsPlatform(platform)) return 'gads';
  return null;
}

// ── TTD key aliasing / entity status ─────────────────────────────────
// Both moved INTO buildTtdReviewRows (ttdRowBuilders.js): it now reads the
// copy template's AdGroupId/AdGroupName spelling directly and derives
// entityStatus from IsEnabled, so the post-push CLW review page renders copy
// templates identically to this grid.

// ── Creative overlay (DV360 / TTD) ───────────────────────────────────
// Copies are SHELL launches (template assets: []), so CLW's 'creative' cell
// would read '—' and imply the copy has no creatives at all. The truth lives
// in creative_plan: {sourceRef: [creativeId, …]} — same-advertiser creative
// ids the backend RE-ASSOCIATES onto the new entities after create, keyed by
// the same source ref the rows use as rowId. Surface that, plus an ⓘ
// annotation chip so the cell is self-explanatory in every column group.
function overlayCreativePlan(rows, launcherResult) {
  const plan = launcherResult?.creative_plan;
  if (!plan || typeof plan !== 'object') return rows;
  return rows.map((row) => {
    const ids = asArray(plan[row.rowId]);
    const creative = ids.length
      ? `${ids.length} creative${ids.length === 1 ? '' : 's'} re-associated`
      : 'none (shell)';
    return {
      ...row,
      creative,
      annotations: {
        ...(row.annotations || {}),
        creative: {
          type: 'i',
          msg: ids.length
            ? `creatives: re-associated — ${ids.length} source creative${ids.length === 1 ? '' : 's'} will be re-attached after the entities are created (same-advertiser ids stay valid).`
            : 'creatives: none (shell) — the source row carried no creatives; the copy launches as a shell.',
        },
      },
    };
  });
}

// ── Google Ads overlay ───────────────────────────────────────────────
// Copy-template facts CLW's gads builder can't know:
//   - Search ad groups carry their URL as `click_url` (the builder only reads
//     ag.final_url before falling back to the campaign), so overlay it;
//   - PMax asset groups DO have a real `name` in the copy template (the
//     builder's assetKey fallback would render "asset_group_1"), so use it
//     for the sticky name column and the assetKey cell — PLAIN, never the
//     builder's "Campaign · key" composite, because the name cell is
//     EDITABLE here and its committed value is written back verbatim as the
//     asset group's name (a composite would bake the campaign prefix in);
//   - headlines/descriptions cells carry the JOINED TEXT (the builder's
//     `headlineList`) instead of the "2 headlines" count label: these cells
//     are editable in the copy grid, and CellDetailPanel seeds its draft
//     from the CELL value — editing a count label would commit the literal
//     string "2 headlines" as the only headline. The counts stay visible in
//     the creativeFormat cell ("2 headlines · 1 description").
// Inclusive day count between two YYYY-MM-DD dates, for deriving a campaign
// total from a daily budget the same way the copy launchers do.
function flightDayCount(startYmd, endYmd) {
  const start = Date.parse(`${String(startYmd || '').slice(0, 10)}T00:00:00Z`);
  const end = Date.parse(`${String(endYmd || '').slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;
  return Math.round((end - start) / 86400000) + 1;
}

function overlayGadsCopyRows(rows, launcherResult, subtype) {
  const campaigns = asArray(launcherResult.campaigns);
  return rows.map((row) => {
    const at = parseGadsRowId(row.rowId);
    if (!at) return row;
    const campaign = campaigns[at.campaignIdx];
    if (!campaign) return row;
    const listAsText = {
      ...(row.headlineList && row.headlineList !== '—' ? { headlines: row.headlineList } : {}),
      ...(row.descriptionList && row.descriptionList !== '—' ? { descriptions: row.descriptionList } : {}),
      // Search keywords are editable text here for the same seeding reason —
      // and NOT on PMax, where the `keywords` cell holds search themes the
      // copy template doesn't carry (so there is nothing to write back to).
      ...(subtype === 'search' && row.keywordList && row.keywordList !== '—'
        ? { keywords: row.keywordList } : {}),
    };
    // BUDGET, un-split. CLW divides a campaign's total across its ad groups
    // so ITS breakdown tables sum to the campaign total — but Google Ads has
    // no ad-group budget, and in the copy flow that split is what the user
    // reads as "the budget I set". Entering $100/day for Aug 14–28 built a
    // $1,500 campaign total and the grid then showed $1,500 on a 1-ad-group
    // campaign and $375 on 4-ad-group ones — three different numbers for one
    // input. Every row of a campaign now shows that campaign's own total
    // (Daily budget column keeps the $100/day it was built from).
    const campaignTotalBudget = (() => {
      const micros = campaign.total_budget_micros;
      if (typeof micros === 'number' && micros > 0) return micros / 1e6;
      const daily = campaign.daily_budget_micros;
      if (typeof daily === 'number' && daily > 0) {
        const days = flightDayCount(campaign.start_date, campaign.end_date);
        if (days) return (daily / 1e6) * days;
      }
      return null;
    })();
    // `siblingCount: 1` — UNCONDITIONALLY, because applyGadsCopyFields always
    // calls applyGadsCampaignFields with the default budgetUnits of 1. This
    // grid does no share × units scaling at all, so the pre-commit preview
    // (GADS_SCALED_FIELDS, driven by row.siblingCount) must not claim one:
    // left at buildGadsReviewRows' unit count it would announce a total N×
    // larger than what actually gets written.
    const budgetCell = {
      siblingCount: 1,
      ...(campaignTotalBudget != null ? { budget: campaignTotalBudget } : {}),
    };
    if (subtype === 'search') {
      const ag = asArray(campaign.ad_groups)[at.unitIdx];
      const url = ag && typeof ag.click_url === 'string' && ag.click_url.trim();
      // MATCH TYPE is stored PER KEYWORD in the copy template
      // ({keyword, match_type}, straight off the importer — see
      // copy_google_ads_campaign_launcher._map_keywords), while CLW's row
      // builder expects one ad-group-level value and so rendered '—' even
      // though the match types copy over correctly. Derive the distinct set
      // from the ad group's own keywords instead. Read-only: a single
      // ad-group write has no meaning against per-keyword values, and the
      // keywords cell already round-trips each keyword's own type.
      const matchTypes = [...new Set(
        asArray(ag && ag.keywords)
          .map((k) => (k && typeof k.match_type === 'string' ? k.match_type.trim().toUpperCase() : ''))
          .filter(Boolean),
      )];
      return {
        ...row,
        ...listAsText,
        ...budgetCell,
        ...(url ? { clickURL: ag.click_url } : {}),
        ...(matchTypes.length ? { matchType: matchTypes.join(' · ') } : {}),
        // COLUMN ORDER: the grid's sticky column always renders `name`, so
        // the campaign goes there (first) and the ad group moves to its own
        // `adGroupName` column (second). Edit routing follows the DISPLAY:
        // `name` writes the campaign, `adGroupName` writes the ad group —
        // see applyGadsCopyFields / the gadsDelegated rename below.
        ...(campaign.name ? { name: campaign.name } : {}),
        adGroupName: (ag && ag.name) || row.name,
      };
    }
    const group = asArray(campaign.asset_groups)[at.unitIdx];
    const campaignNameCell = campaign.name ? { name: campaign.name } : {};
    if (!group || !group.name) {
      return { ...row, ...listAsText, ...budgetCell, ...campaignNameCell,
        adGroupName: row.name };
    }
    return {
      ...row, ...listAsText, ...budgetCell, ...campaignNameCell,
      assetKey: group.name, adGroupName: group.name,
    };
  });
}

// ── DV360 targeting_plan → read-only display cells ───────────────────
// The copy TEMPLATE deliberately carries no targeting keys (the validator's
// allowlist forbids them) — line-item targeting rides launcherResult
// .targeting_plan instead: {sourceLineItemRef: [{targetingType,
// assignedTargetingOptions: [{<type>Details: {...}}, ...]}, ...]}, applied
// verbatim post-create via bulkEditAssignedTargetingOptions (see
// copy_dv360_campaign_launcher.py's _extract_targeting_plan — each option is
// the raw DV360 AssignedTargetingOption minus read-only fields, so its one
// remaining key is the per-type `<x>Details` object). The refs are the same
// source lineItemIds the rows use as rowId, so the plan maps 1:1 onto rows.
//
// These cells are editable BY REMOVAL only: pruning entries from the plan is
// deterministic (removeTargetingOption/removeTargetingType below), while
// ADDING targeting would need DV360 option-id lookups and stays out of
// scope. None of them take the scalar cell editor (not in
// COPY_DV360_EDITABLE) — removal flows through the detail panel's ×
// buttons / the copy-folder removal panel instead, and CopyPlanStep's
// per-grid callout states the contract once rather than per cell.

// The one `<x>Details` payload of an assigned-targeting option.
const optionDetails = (option) => {
  if (!option || typeof option !== 'object') return {};
  const key = Object.keys(option).find((k) => k.endsWith('Details') && option[k] && typeof option[k] === 'object');
  return key ? option[key] : {};
};

// Stable identity of one plan option, used by BOTH the panel entries the
// derivation emits and the removal appliers below — so what the UI offers to
// remove is exactly what the applier matches. Only geo/POI and inventory
// sources carry a real targetingOptionId after the launcher's read-only-field
// strip; every other type is identified by its one distinguishing detail
// value (keyword text, device enum, …), with the serialized details as the
// last resort so even an unrecognized shape stays individually addressable.
export function targetingOptionKey(option) {
  const d = optionDetails(option);
  const key = d.targetingOptionId ?? d.inventorySourceId ?? d.inventorySourceGroupId
    ?? d.keyword ?? d.deviceType ?? d.environment ?? d.viewability
    ?? d.excludedContentRatingTier ?? d.excludedSensitiveCategory ?? d.displayName;
  return key != null ? String(key) : JSON.stringify(d);
}

// Grid field → the plan targetingType(s) it renders. Drives per-field
// remove-all and the deletion resolver. `interests` shares
// TARGETING_TYPE_AUDIENCE_GROUP with `audiences` (Google-audience settings
// nest INSIDE the same option), so interests are always pruned per-id, never
// by dropping the whole type.
export const DV360_TARGETING_FIELD_TYPES = {
  geo: ['TARGETING_TYPE_GEO_REGION', 'TARGETING_TYPE_POI', 'TARGETING_TYPE_REGIONAL_LOCATION_LIST'],
  keywords: ['TARGETING_TYPE_KEYWORD'],
  audiences: ['TARGETING_TYPE_AUDIENCE_GROUP'],
  interests: ['TARGETING_TYPE_AUDIENCE_GROUP'],
  pmp: ['TARGETING_TYPE_INVENTORY_SOURCE', 'TARGETING_TYPE_INVENTORY_SOURCE_GROUP'],
  devices: ['TARGETING_TYPE_DEVICE_TYPE'],
  environment: ['TARGETING_TYPE_ENVIRONMENT'],
  viewability: ['TARGETING_TYPE_VIEWABILITY'],
  brandSafety: ['TARGETING_TYPE_DIGITAL_CONTENT_LABEL_EXCLUSION', 'TARGETING_TYPE_SENSITIVE_CATEGORY_EXCLUSION'],
};

// Types DV360 pre-assigns a DEFAULT for on line-item creation — the ones
// copy_dv360_campaign_launcher.py RECONCILES instead of plainly creating
// (_SINGLETON_TARGETING_TYPES, plus DEVICE_TYPE via _ENUM_TARGETING_TYPES). The
// launch makes the line item match whatever the plan still lists, so removing
// ONE option (one device) takes it off the copy; removing the whole entry
// leaves nothing to match, so the new line item keeps DV360's default — NOT
// "no targeting" — which the removal panel footnotes for these types.
export const DV360_SINGLETON_TARGETING_TYPES = new Set([
  'TARGETING_TYPE_VIEWABILITY', 'TARGETING_TYPE_AUTHORIZED_SELLER_STATUS',
  'TARGETING_TYPE_ON_SCREEN_POSITION', 'TARGETING_TYPE_DEVICE_TYPE',
]);

// 'DEVICE_TYPE_SMART_PHONE' → 'smart phone' (generic enum-tail humanizer).
const enumTail = (value, prefix) => String(value || '')
  .replace(prefix, '').toLowerCase().replace(/_/g, ' ').trim();

// ['Atlanta', 'Chicago', …] → 'Atlanta, Chicago +7' (graceful truncation).
const joinTruncated = (labels, max = 3) => (labels.length <= max
  ? labels.join(', ')
  : `${labels.slice(0, max).join(', ')} +${labels.length - max}`);

// Returns {cells, entries}:
//   cells   — the display values overlaid onto the grid row;
//   entries — per grid field, the removable plan entries behind those cells,
//             each {label, targetingType, optionKey, excluded} — `label` is
//             exactly the string the cell detail panel lists (its × buttons
//             hand labels back through onToggleListDeletion), `optionKey` is
//             the applier-side identity (targetingOptionKey above). This is
//             what makes targeting editable BY REMOVAL: pruning entries is
//             deterministic; ADDING targeting needs DV360 option-id lookups
//             and stays out of scope.
function deriveDv360Targeting(planEntries) {
  const optionsByType = new Map();
  for (const entry of asArray(planEntries)) {
    if (!entry?.targetingType) continue;
    const list = optionsByType.get(entry.targetingType) || [];
    list.push(...asArray(entry.assignedTargetingOptions));
    optionsByType.set(entry.targetingType, list);
  }
  if (optionsByType.size === 0) return { cells: {}, entries: {} };
  const of = (type) => optionsByType.get(type) || [];
  const cells = {};
  const entries = {};
  const entriesOf = (field, types, labelOf, excludedOf = () => false) => {
    const list = types.flatMap((type) => of(type).map((o) => ({
      label: labelOf(o),
      targetingType: type,
      optionKey: targetingOptionKey(o),
      excluded: excludedOf(o),
    }))).filter((e) => e.label);
    if (list.length) entries[field] = list;
    return list;
  };

  // Geo — GEO_REGION (+ POI/regional variants) displayNames, negatives split
  // out the way the CLW rows spell them (geo summary + include/exclude lists).
  const geoEntries = entriesOf(
    'geo',
    DV360_TARGETING_FIELD_TYPES.geo,
    (o) => optionDetails(o).displayName || `geo ${targetingOptionKey(o)}`,
    (o) => Boolean(optionDetails(o).negative),
  );
  if (geoEntries.length) {
    const include = geoEntries.filter((e) => !e.excluded);
    const exclude = geoEntries.filter((e) => e.excluded);
    const incLabels = include.map((e) => e.label);
    const excLabels = exclude.map((e) => e.label);
    const incSummary = incLabels.length ? joinTruncated(incLabels) : null;
    if (incSummary) {
      cells.geo = exclude.length ? `${incSummary} · excl. ${exclude.length}` : incSummary;
    } else if (exclude.length) {
      cells.geo = `excl. ${exclude.length} geo target${exclude.length === 1 ? '' : 's'}`;
    }
    cells.geoIncludeList = incLabels.length ? incLabels.join(' · ') : cells.geo || '—';
    cells.geoExcludeList = excLabels.length ? excLabels.join(' · ') : '—';
  }

  // Keywords — {keywordDetails: {keyword, negative}}. Cell/panel list the
  // positives; negative keyword options remain removable via remove-all.
  const kwEntries = entriesOf(
    'keywords',
    DV360_TARGETING_FIELD_TYPES.keywords,
    (o) => optionDetails(o).keyword || null,
    (o) => Boolean(optionDetails(o).negative),
  );
  if (kwEntries.length) {
    const kws = kwEntries.filter((e) => !e.excluded).map((e) => e.label);
    cells.keywords = `${kws.length} keyword${kws.length === 1 ? '' : 's'}`;
    cells.keywordList = kws.length ? kws.join(' · ') : '—';
    cells.keywordCount = kws.length;
  }

  // Audiences — audienceGroupDetails carries per-kind groups of id settings.
  // Entries are PER ID (optionKey = the audience id): the removal applier
  // prunes the id from inside the shared AUDIENCE_GROUP option rather than
  // dropping the whole option.
  const audOptions = of('TARGETING_TYPE_AUDIENCE_GROUP');
  if (audOptions.length) {
    let audienceIds = [];
    let googleIds = [];
    for (const o of audOptions) {
      const d = optionDetails(o);
      for (const [key, group] of Object.entries(d)) {
        const groups = Array.isArray(group) ? group : [group];
        const settings = groups.flatMap((g) => asArray(g?.settings));
        const ids = settings
          .map((s) => s?.firstPartyAndPartnerAudienceId || s?.thirdPartyAudienceId
            || s?.customListId || s?.combinedAudienceId || s?.googleAudienceId)
          .filter(Boolean);
        if (key === 'includedGoogleAudienceGroup') googleIds = googleIds.concat(ids);
        else if (key.startsWith('included')) audienceIds = audienceIds.concat(ids);
      }
    }
    const idEntry = (id) => ({
      label: String(id), targetingType: 'TARGETING_TYPE_AUDIENCE_GROUP',
      optionKey: String(id), excluded: false,
    });
    if (audienceIds.length) {
      cells.audiences = `${audienceIds.length} audience${audienceIds.length === 1 ? '' : 's'}`;
      cells.audienceList = audienceIds.map(String).join(' · ');
      entries.audiences = audienceIds.map(idEntry);
    } else {
      cells.audiences = `${audOptions.length} audience group${audOptions.length === 1 ? '' : 's'}`;
    }
    if (googleIds.length) {
      cells.interests = `${googleIds.length} Google audience${googleIds.length === 1 ? '' : 's'}`;
      cells.interestIds = googleIds;
      entries.interests = googleIds.map(idEntry);
    }
  }

  // PMP / inventory — source + group assignments.
  const pmpEntries = entriesOf(
    'pmp',
    DV360_TARGETING_FIELD_TYPES.pmp,
    (o) => {
      const d = optionDetails(o);
      if (d.inventorySourceId != null) return String(d.inventorySourceId);
      if (d.inventorySourceGroupId != null) return `group ${d.inventorySourceGroupId}`;
      return targetingOptionKey(o);
    },
  );
  if (pmpEntries.length) {
    const sources = of('TARGETING_TYPE_INVENTORY_SOURCE');
    const groups = of('TARGETING_TYPE_INVENTORY_SOURCE_GROUP');
    const parts = [];
    if (sources.length) parts.push(`${sources.length} inventory source${sources.length === 1 ? '' : 's'}`);
    if (groups.length) parts.push(`${groups.length} group${groups.length === 1 ? '' : 's'}`);
    cells.pmp = parts.join(' · ');
    cells.pmpDealCount = sources.length;
    const ids = sources.map((o) => optionDetails(o).inventorySourceId).filter(Boolean);
    if (ids.length) cells.pmpDealList = ids.map(String).join(' · ');
  }

  // Devices / environment — enum lists, humanized.
  const deviceEntries = entriesOf(
    'devices', DV360_TARGETING_FIELD_TYPES.devices,
    (o) => enumTail(optionDetails(o).deviceType, /^DEVICE_TYPE_/) || null,
  );
  if (deviceEntries.length) cells.devices = deviceEntries.map((e) => e.label).join(', ');
  const envEntries = entriesOf(
    'environment', DV360_TARGETING_FIELD_TYPES.environment,
    (o) => enumTail(optionDetails(o).environment, /^ENVIRONMENT_/) || null,
  );
  if (envEntries.length) cells.environment = envEntries.map((e) => e.label).join(', ');

  // Viewability — 'VIEWABILITY_70_PERCENT_OR_MORE' → '70%+'.
  const viewEntries = entriesOf(
    'viewability', DV360_TARGETING_FIELD_TYPES.viewability,
    (o) => {
      const v = optionDetails(o).viewability;
      if (!v) return null;
      const m = /_(\d+)_PERCENT/.exec(String(v));
      return m ? `${m[1]}%+` : enumTail(v, /^VIEWABILITY_/);
    },
  );
  if (viewEntries.length) cells.viewability = viewEntries[0].label;

  // Brand safety — content-label + sensitive-category exclusions.
  const safetyEntries = entriesOf(
    'brandSafety', DV360_TARGETING_FIELD_TYPES.brandSafety,
    (o) => {
      const d = optionDetails(o);
      return enumTail(d.excludedContentRatingTier, /^CONTENT_RATING_TIER_/)
        || enumTail(d.excludedSensitiveCategory, /^SENSITIVE_CATEGORY_/)
        || targetingOptionKey(o);
    },
  );
  if (safetyEntries.length) {
    cells.brandSafety = `${safetyEntries.length} content exclusion${safetyEntries.length === 1 ? '' : 's'}`;
  }

  return { cells, entries };
}

// True for the "nothing here" values a copy-template row renders when the
// underlying key is absent — the overlays below only fill THOSE, never a
// value the template actually stated.
const isEmptyCell = (v) => v == null || v === '—' || v === '';

// Per-key variant: CLW's DV360 builder fabricates a couple of DEFAULT labels
// instead of '—' when the underlying key is absent ('all devices',
// 'Standard exclusions') — on a copy template those are placeholders too,
// so the real targeting-plan values must be allowed to replace them.
const isPlaceholderCell = (key, v) => isEmptyCell(v)
  || (key === 'devices' && v === 'all devices')
  || (key === 'brandSafety' && v === 'Standard exclusions');

function overlayDv360PlanRows(rows, launcherResult) {
  const plan = launcherResult?.targeting_plan;
  const liById = new Map(asArray(launcherResult.lineItems).map((li) => [li.lineItemId, li]));
  return rows.map((row) => {
    const out = { ...row };
    // cpm — the CLW builder derives it from package rates (absent on copy
    // templates); the template's own bid micros are the honest number.
    if (isEmptyCell(out.cpm)) {
      const li = liById.get(row.rowId);
      const bidObj = li?.bidStrategy || {};
      const detail = bidObj[Object.keys(bidObj)[0]] || {};
      const micros = detail.maxAverageCpmBidAmountMicros ?? detail.bidAmountMicros;
      if (micros != null) out.cpm = Number(micros) / 1e6;
    }
    if (plan && typeof plan === 'object') {
      const { cells, entries } = deriveDv360Targeting(plan[row.rowId]);
      for (const [key, value] of Object.entries(cells)) {
        // List/count companions always follow their summary cell; summary
        // cells only fill placeholders, never a template-stated value.
        if (isPlaceholderCell(key, out[key]) || key.endsWith('List') || key === 'keywordCount'
          || key === 'interestIds' || key === 'pmpDealCount') {
          out[key] = value;
        }
      }
      // The removable plan entries behind each cell — the removal panel and
      // the deletion resolver in applyCopyReviewEdits both read these.
      if (Object.keys(entries).length) out.targetingEntries = entries;
    }
    overlayDv360PendingGeo(out, launcherResult?.geo_plan?.[row.rowId]);
    return out;
  });
}

// Geo names ADDED in this grid live in `geo_plan[lineItemId]`, not in
// targeting_plan: DV360 needs a targetingOptionId and only the launcher can get
// one (search_geo_region, a service-side lookup), so the name is carried and
// resolved at launch. They must still RENDER here — a cell that silently
// ignores what the user just typed is the same class of bug as an edit with no
// applier, and the grid re-rendering unchanged is exactly how it presents.
//
// Pending names are appended to the include/exclude lists with a marker, so the
// panel lists them (and can remove them) alongside the resolved entries.
export const PENDING_GEO_SUFFIX = ' (resolves at launch)';

function overlayDv360PendingGeo(out, spec) {
  if (!spec || typeof spec !== 'object') return;
  // `additive` is what separates the two writers of geo_plan, and they mean
  // opposite things. A grid add (additive: true) keeps the line item's resolved
  // GEO_REGION options and appends. A STEP-1 override arrives with no flag, and
  // _resolve_and_merge_geo strips every GEO_REGION createRequest before applying
  // it — so the source markets will NOT be targeted. Listing them next to the
  // pending name would report targeting that is about to be thrown away, which
  // is the same class of bug as swallowing an edit: the cell states something
  // the launch will not do.
  if (!spec.additive) {
    out.geoIncludeList = '—';
    out.geoExcludeList = '—';
    // Drop the source geo from the removal panel too. CopyPlanStep opens
    // Dv360TargetingPanel off `targetingEntries.geo` (keyed by the
    // DV360_TARGETING_FIELD_TYPES name, NOT by the geoInclude/geoExclude edit
    // sub-fields), so leaving it would offer × on markets the cell no longer
    // lists and the launch will not target — an edit whose effect is invisible,
    // which is the thing this overlay exists to stop. Every other targeting
    // type's entries are untouched.
    if (out.targetingEntries?.geo) {
      const rest = { ...out.targetingEntries };
      delete rest.geo;
      if (Object.keys(rest).length) out.targetingEntries = rest;
      else delete out.targetingEntries;
    }
  }
  const append = (listKey, names) => {
    const pending = asArray(names).map((n) => String(n).trim()).filter(Boolean);
    if (!pending.length) return;
    const marked = pending.map((n) => `${n}${PENDING_GEO_SUFFIX}`);
    const current = out[listKey];
    out[listKey] = (current && current !== '—')
      ? `${current} · ${marked.join(' · ')}` : marked.join(' · ');
  };
  append('geoIncludeList', spec.included);
  append('geoExcludeList', spec.excluded);
  // Keep the summary cell honest about the new count.
  const incCount = (out.geoIncludeList && out.geoIncludeList !== '—')
    ? out.geoIncludeList.split(' · ').length : 0;
  const excCount = (out.geoExcludeList && out.geoExcludeList !== '—')
    ? out.geoExcludeList.split(' · ').length : 0;
  if (incCount || excCount) {
    const incSummary = incCount
      ? joinTruncated(out.geoIncludeList.split(' · ')) : null;
    out.geo = incSummary
      ? (excCount ? `${incSummary} · excl. ${excCount}` : incSummary)
      : `excl. ${excCount} geo target${excCount === 1 ? '' : 's'}`;
  }
}

// ── DV360 targeting removal (editing BY PRUNING the plan) ────────────
// The plan is applied verbatim at launch from the EDITED launcherResult, so
// removing an entry here genuinely removes it from the new line item.

// Prune one audience id from inside an AUDIENCE_GROUP option's nested
// groups, in place. Returns true when something was removed. Drops emptied
// groups/keys so the option's details shrink honestly.
function pruneAudienceIdInPlace(option, id) {
  const details = optionDetails(option);
  let removed = false;
  const settingsWithout = (settings) => asArray(settings).filter((s) => {
    const sid = s?.firstPartyAndPartnerAudienceId || s?.thirdPartyAudienceId
      || s?.customListId || s?.combinedAudienceId || s?.googleAudienceId;
    if (String(sid) === String(id)) { removed = true; return false; }
    return true;
  });
  for (const key of Object.keys(details)) {
    const group = details[key];
    if (Array.isArray(group)) {
      const next = group
        .map((g) => (g && Array.isArray(g.settings) ? { ...g, settings: settingsWithout(g.settings) } : g))
        .filter((g) => !(g && Array.isArray(g.settings) && g.settings.length === 0));
      details[key] = next;
      if (next.length === 0) delete details[key];
    } else if (group && Array.isArray(group.settings)) {
      group.settings = settingsWithout(group.settings);
      if (group.settings.length === 0) delete details[key];
    }
  }
  return removed;
}

// Shared in-place core for the two exported appliers and the deletions path
// in applyCopyReviewEdits (which owns its own deep clone already).
function removeTargetingOptionInPlace(edited, ref, targetingType, optionKey) {
  const plan = edited?.targeting_plan?.[ref];
  if (!Array.isArray(plan)) return;
  const groupIdx = plan.findIndex((g) => g?.targetingType === targetingType);
  if (groupIdx === -1) return;
  const group = plan[groupIdx];
  const before = asArray(group.assignedTargetingOptions);
  let after = before.filter((o) => targetingOptionKey(o) !== String(optionKey));
  if (after.length === before.length && targetingType === 'TARGETING_TYPE_AUDIENCE_GROUP') {
    // Not an option-level match — try the nested audience-id prune (audience
    // and Google-audience ids live INSIDE one shared option's groups).
    after = before
      .filter((o) => {
        pruneAudienceIdInPlace(o, optionKey);
        // An option whose details emptied out entirely has nothing left to
        // create — drop it with the same rule the group/ref cleanup uses.
        return Object.keys(optionDetails(o)).length > 0;
      });
  }
  group.assignedTargetingOptions = after;
  if (after.length === 0) plan.splice(groupIdx, 1);
  if (plan.length === 0) delete edited.targeting_plan[ref];
}

function removeTargetingTypeInPlace(edited, ref, targetingType) {
  const plan = edited?.targeting_plan?.[ref];
  if (!Array.isArray(plan)) return;
  const next = plan.filter((g) => g?.targetingType !== targetingType);
  if (next.length === plan.length) return;
  edited.targeting_plan[ref] = next;
  if (next.length === 0) delete edited.targeting_plan[ref];
}

/**
 * Remove ONE plan entry from targeting_plan[ref]'s createRequest group of
 * `targetingType`. `optionKey` is targetingOptionKey's identity (a real
 * targetingOptionId where DV360 kept one, the distinguishing detail value
 * otherwise, or — for AUDIENCE_GROUP — an audience id, pruned from inside
 * the option's nested groups). Emptied groups are dropped; an emptied
 * targeting_plan[ref] is dropped too. Unknown refs/types/keys are a no-op
 * returning a structurally identical copy. Pure — returns a new deep clone.
 */
export function removeTargetingOption(launcherResult, ref, targetingType, optionKey) {
  const edited = JSON.parse(JSON.stringify(launcherResult || {}));
  removeTargetingOptionInPlace(edited, ref, targetingType, optionKey);
  return edited;
}

/**
 * Remove targeting_plan[ref]'s WHOLE createRequest group of `targetingType`
 * (the panel's remove-all). Same cleanup + no-op semantics as
 * removeTargetingOption. Pure — returns a new deep clone.
 */
export function removeTargetingType(launcherResult, ref, targetingType) {
  const edited = JSON.parse(JSON.stringify(launcherResult || {}));
  removeTargetingTypeInPlace(edited, ref, targetingType);
  return edited;
}

// The cell-detail panel's × buttons hand back (subField, label); resolve the
// label to a plan entry through the ROW's targetingEntries (built by the same
// derivation the panel rendered from, so labels can't drift) and prune it.
const DV360_DELETION_FIELD_MAP = {
  geoInclude: { field: 'geo', excluded: false },
  geoExclude: { field: 'geo', excluded: true },
  keywords: { field: 'keywords', excluded: false },
  audiences: { field: 'audiences', excluded: false },
  interests: { field: 'interests', excluded: false },
  pmp: { field: 'pmp', excluded: false },
  devices: { field: 'devices', excluded: false },
  environment: { field: 'environment', excluded: false },
  viewability: { field: 'viewability', excluded: false },
  brandSafety: { field: 'brandSafety', excluded: false },
};

// `fieldOps` is the normalized { field: [{op, value, newValue?}] } shape. Only
// REMOVE ops act here: adding or renaming a plan entry would need a DV360
// targetingOptionId the grid has no way to look up (COPY_DV360_LIST_FIELDS
// states that reason in the panel), so an add/edit leaves the plan untouched
// and CopyPlanStep's unchanged-template guard skips the save.
function applyDv360TargetingDeletions(edited, rowId, fieldOps, reviewRows) {
  const row = reviewRows.find((r) => r.rowId === rowId);
  for (const [subField, ops] of Object.entries(fieldOps || {})) {
    // Geo is the one DV360 field that also accepts ADDS: the name is carried in
    // geo_plan and resolved to a targetingOptionId at launch. Removing a name
    // that is still pending takes it back out of geo_plan; removing a resolved
    // entry prunes targeting_plan as every other field does.
    if (subField === 'geoInclude' || subField === 'geoExclude') {
      applyDv360GeoOps(edited, rowId, subField, ops);
    }
    const entriesByField = row?.targetingEntries;
    if (!entriesByField) continue;
    const mapping = DV360_DELETION_FIELD_MAP[subField];
    if (!mapping) continue;
    const fieldEntries = asArray(entriesByField[mapping.field]);
    for (const op of ops) {
      if (op.op !== 'remove') continue;
      const entry = fieldEntries.find((e) => e.label === op.value && e.excluded === mapping.excluded)
        || fieldEntries.find((e) => e.label === op.value);
      if (!entry) continue;
      removeTargetingOptionInPlace(edited, rowId, entry.targetingType, entry.optionKey);
    }
  }
}

/**
 * Geo adds/removes for one DV360 row, against `geo_plan[rowId]`.
 *
 * `additive: true` is what makes this safe: the launcher keeps the line item's
 * existing GEO_REGION option ids and only resolves the names added here, so an
 * add can never retarget what is already there (see
 * copy_dv360_campaign_launcher._resolve_and_merge_geo). Without it the launcher
 * REPLACES the row's geo, which would mean round-tripping every existing region
 * back through a fuzzy name search just to re-state it.
 *
 * The one entry that is deliberately NOT additive is a step-1 wizard override,
 * which states the whole desired set by name and is meant to replace the source
 * geo. An add on such a row extends that set and stays a replacement — see the
 * comment on `existing` below.
 */
function applyDv360GeoOps(edited, rowId, subField, ops) {
  const key = subField === 'geoExclude' ? 'excluded' : 'included';
  const strip = (v) => String(v || '').replace(PENDING_GEO_SUFFIX, '').trim();
  for (const op of asArray(ops)) {
    if (op.op === 'add') {
      const name = strip(op.value);
      if (!name) continue;
      if (!edited.geo_plan || typeof edited.geo_plan !== 'object') edited.geo_plan = {};
      // Default a NEW entry to additive — a grid add on its own must never
      // retarget the resolved regions it cannot even name (see above).
      //
      // But do NOT stamp it onto an entry that is already there: a step-1
      // override carries no flag and means "target exactly these", so flipping
      // it here would silently widen the launch to the source geo PLUS both
      // names. Extend the override's list and leave its replace semantics alone.
      const existing = edited.geo_plan[rowId];
      const spec = existing || { included: [], excluded: [], additive: true };
      spec.included = asArray(spec.included);
      spec.excluded = asArray(spec.excluded);
      if (!spec[key].includes(name)) spec[key].push(name);
      edited.geo_plan[rowId] = spec;
    } else if (op.op === 'remove') {
      const spec = edited.geo_plan?.[rowId];
      if (!spec) continue;
      const name = strip(op.value);
      const next = asArray(spec[key]).filter((n) => String(n).trim() !== name);
      if (next.length === asArray(spec[key]).length) continue; // not a pending name
      spec[key] = next;
      // Drop an entry that no longer carries any name, and the map with it, so
      // the template never ships an empty override.
      if (!asArray(spec.included).length && !asArray(spec.excluded).length) {
        delete edited.geo_plan[rowId];
        if (!Object.keys(edited.geo_plan).length) delete edited.geo_plan;
      }
    } else if (op.op === 'edit') {
      // Re-typing a pending name is a remove + add of the new value. When the
      // entry's LAST name is retyped the remove drops the whole entry, so the
      // add recreates it and picks up the additive DEFAULT — which would turn a
      // step-1 "target only these" back into "source geo plus these" on a pure
      // rename. Carry the replacement semantics across the round trip.
      const before = edited.geo_plan?.[rowId];
      const wasReplacement = Boolean(before) && !before.additive;
      applyDv360GeoOps(edited, rowId, subField, [
        { op: 'remove', value: op.value },
        { op: 'add', value: op.newValue },
      ]);
      if (wasReplacement && edited.geo_plan?.[rowId]) delete edited.geo_plan[rowId].additive;
    }
  }
}

// TTD copy-template fallbacks the SHARED builder can't provide. Tactic
// derivation, deal counts / geo from review_enrichment, ThirdPartyAudienceId
// → audiences, and IsEnabled → entityStatus all moved INTO
// buildTtdReviewRows (ttdRowBuilders.js) so the post-push CLW review page
// renders copy templates identically — see that file. What remains here is
// the one label only this grid wants: an ad group with bid lists but no
// resolvable deals/inventory type still shows how many bid lists ride along.
function overlayTtdPlanRows(rows, launcherResult) {
  const agById = new Map(asArray(launcherResult.AdGroups)
    .map((ag) => [ag.AdGroupId ?? ag.adGroupId, ag]));
  return rows.map((row) => {
    const ag = agById.get(row.rowId);
    if (!ag) return row;
    // Geo first, unconditionally — the bid-list COUNT label below returns early
    // for a row that already has a pmp value, which would skip a geo edit.
    const out = overlayTtdEditedGeo({ ...row }, launcherResult, row.rowId);
    const bidLists = asArray(ag.AssociatedBidLists);
    if (!isEmptyCell(row.pmp) || !bidLists.length) return out;
    return {
      ...out,
      pmp: `${bidLists.length} bid list${bidLists.length === 1 ? '' : 's'}`,
      pmpDealCount: bidLists.length,
    };
  });
}

// A geo edit takes ownership of the row's geo (see applyTtdGeoOps), so once
// `*_edited` exists it — not the source's bid lists — is what launches, and it
// is what the cell must show. Rendering the stale source list would tell the
// user their edit didn't take, which is the failure mode this grid has had too
// much of.
function overlayTtdEditedGeo(row, launcherResult, rowId) {
  const enrich = (launcherResult?.review_enrichment || {})[rowId] || {};
  const inc = Array.isArray(enrich.geo_include_edited) ? enrich.geo_include_edited : null;
  const exc = Array.isArray(enrich.geo_exclude_edited) ? enrich.geo_exclude_edited : null;
  if (!inc && !exc) return row;
  const incLabels = (inc || []).map((e) => e.label);
  const excLabels = (exc || []).map((e) => e.label);
  if (inc) row.geoIncludeList = incLabels.length ? incLabels.join(' · ') : '—';
  if (exc) row.geoExcludeList = excLabels.length ? excLabels.join(' · ') : '—';
  // Keep the summary cell consistent with the two lists.
  const incNow = (row.geoIncludeList && row.geoIncludeList !== '—')
    ? row.geoIncludeList.split(' · ') : [];
  const excNow = (row.geoExcludeList && row.geoExcludeList !== '—')
    ? row.geoExcludeList.split(' · ') : [];
  if (incNow.length) {
    row.geo = excNow.length
      ? `${joinTruncated(incNow)} · excl. ${excNow.length}` : joinTruncated(incNow);
  } else if (excNow.length) {
    row.geo = `excl. ${excNow.length} geo target${excNow.length === 1 ? '' : 's'}`;
  } else {
    row.geo = '—';
  }
  return row;
}

/**
 * launcherResult → ReviewGrid rows, one per line item / ad group / asset
 * group. Same row vocabulary as CLW (rowId/name/fmt/start/end/budget/… —
 * see clwGridConstants.GROUP_COLS and gadsConstants group cols), so the
 * shared grid, cell detail panel and formatters work unchanged.
 */
export function buildCopyReviewRows(launcherResult) {
  const family = copyResultFamily(launcherResult);
  if (!family) return [];
  if (family === 'ttd') {
    // buildTtdReviewRows reads both template spellings and handles
    // entityStatus / tactic / geo / deals / audiences itself now (see
    // ttdRowBuilders.js) — the pre-alias and status overlay are gone.
    return overlayCreativePlan(
      overlayTtdPlanRows(buildTtdReviewRows(launcherResult), launcherResult),
      launcherResult,
    );
  }
  if (family === 'gads') {
    const subtype = gadsResultSubtype(launcherResult);
    return overlayGadsCopyRows(buildGadsReviewRows(launcherResult), launcherResult, subtype);
  }
  return overlayCreativePlan(
    overlayDv360PlanRows(buildDv360ReviewRows(launcherResult), launcherResult),
    launcherResult,
  );
}

/**
 * Every campaign name the launcherResult will create, per platform. Drives
 * the push step's changeset table and the unique-name suggestion's
 * collide-set (a rename must not land on a sibling build's name).
 */
/**
 * The SOURCE ACCOUNT'S billing currency, off the built template — the one fact that lets
 * every screen label money the way the platform will. Every amount on a copy template is
 * denominated in it, and the copiers carry it forward from the import:
 *
 *   gads (Search + PMax)  campaigns[].currency_code  (customer.currency_code)
 *   ttd                   Campaigns[].Budget.CurrencyCode / DailyBudget.CurrencyCode
 *   facebook              campaign.currency_code  (AdAccount.currency, carried by
 *                         facebook_campaign_importer_launcher as account_currency)
 *   dv360                 none — copies are IMPRESSION-budgeted (BUDGET_UNIT_IMPRESSIONS)
 *                         and the launcher stamps no currency
 *
 * Returns null when the template states nothing, which callers render as the legacy '$'.
 * Never guesses: a WRONG symbol on real money is worse than an unlabelled number.
 */
export function currencyFromLauncherResult(launcherResult) {
  if (!launcherResult) return null;
  const family = copyResultFamily(launcherResult);
  const clean = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  // Facebook is matched on the platform string, NOT through copyResultFamily, which
  // returns null for 'fb' — the copy grid has no Facebook row builder (see
  // buildCopyReviewRows). Its money still reaches the Push step's summary, and that is
  // what this feeds. Read before the family switch so a future fb family can't change
  // the answer.
  if (String(launcherResult.platform || launcherResult.Platform || '').toLowerCase() === 'fb') {
    return clean(launcherResult.campaign?.currency_code);
  }
  if (family === 'gads') {
    for (const c of asArray(launcherResult.campaigns)) {
      const code = clean(c?.currency_code);
      if (code) return code;
    }
    return null;
  }
  if (family === 'ttd') {
    for (const c of asArray(launcherResult.Campaigns)) {
      const code = clean(c?.Budget?.CurrencyCode) || clean(c?.DailyBudget?.CurrencyCode);
      if (code) return code;
    }
    return null;
  }
  return null;
}

export function campaignNamesFromLauncherResult(launcherResult) {
  const family = copyResultFamily(launcherResult);
  if (family === 'ttd') {
    return asArray(launcherResult.Campaigns).map((c) => c?.CampaignName).filter(Boolean);
  }
  if (family === 'gads') {
    return asArray(launcherResult.campaigns).map((c) => c?.name).filter(Boolean);
  }
  if (family === 'dv360') {
    const name = launcherResult.campaign?.displayName;
    return name ? [name] : [];
  }
  return [];
}

// ── DV360 writers ────────────────────────────────────────────────────
// DV360's validator rejects unknown keys, so every writer below only changes
// the VALUE of a key the copy template is guaranteed to carry (see
// copy_dv360_campaign_launcher.py's _build_line_item/_build_io/_build_campaign
// — pacing/entityStatus/frequencyCap/budgetSegments are all always emitted).
// Enum-valued fields only accept recognized values: an unrecognized token is
// a NO-OP, and the grid re-rendering the unchanged value is what tells the
// user the edit didn't take. The writers themselves live in
// CampaignLaunchWorkflow/sharedFieldWriters.js (shared with CLW's applier).

function applyDv360CopyFields(edited, rowId, fields) {
  const li = asArray(edited.lineItems).find((x) => x.lineItemId === rowId);
  if (!li) return;
  const io = asArray(edited.insertionOrders)
    .find((x) => asArray(x.lineItems).includes(rowId));

  if (fields.name != null && String(fields.name).trim()) {
    li.displayName = String(fields.name).trim();
  }
  // `tactic` is on the validator's line-item allowlist and survives the
  // preprocessor's li_min, but the line-item CREATOR strips it before the
  // DV360 API call (it only ever fed MMGY post-processing) — so on a copy this
  // is a REVIEW-SIDE label that round-trips into the cell and changes nothing
  // at launch. Editable because the row builder reads exactly this key.
  if (fields.tactic != null && String(fields.tactic).trim()) {
    li.tactic = String(fields.tactic).trim();
  }
  // Strategy swap FIRST so a bidFloor/bidGoal edit in the same batch lands
  // inside the new wrapper key rather than the old one (same ordering as
  // CLW's applier).
  if (fields.bidStrategy != null) applyDv360BidStrategy(li, fields.bidStrategy);
  // Same micros-key preference as CLW's applier (shared writer): the copy
  // template always carries a bidStrategy key, so the value-only contract
  // holds.
  if (fields.bidFloor != null) writeDv360BidMicros(li, fields.bidFloor);
  if (fields.bidGoal != null) applyDv360BidGoal(li, fields.bidGoal);
  // targetingExpansion / conversionCounting are both on the validator's
  // line-item allowlist and both survive li_min, so they are real launch
  // edits — not just review labels. Pixel before convWindow so a floodlight
  // config created by the pixel edit is there for the window edit to refine.
  if (fields.expansion != null) applyDv360Expansion(li, fields.expansion);
  if (fields.pixel != null) applyDv360Pixel(li, fields.pixel, fields.convWindow);
  if (fields.convWindow != null) applyDv360ConvWindow(li, fields.convWindow);
  // Daily pacing cap. Read path (buildDv360ReviewRows) prefers dailyMaxMicros
  // over dailyMaxImpressions, so write whichever key the template carries —
  // the grid then round-trips the exact number the user typed with no unit
  // conversion (the launcher stores impression caps under dailyMaxMicros on
  // some plans, per CLW's own comment).
  if (fields.dailyCap != null) writeDv360DailyCap(li, fields.dailyCap);
  // Pacing type/period — enum values on a key the template always carries; see
  // sharedFieldWriters.js for the accepted tokens.
  if (fields.pacing != null) applyDv360Pacing(li, fields.pacing);
  // NO entityStatus WRITE — `entityStatus` is denylisted in
  // COPY_DV360_DENYLIST and refused here too. Activation is the launch action's
  // job, not a cell, and this grid saves every edit IMMEDIATELY with no undo.
  if (fields.freqCap != null) applyDv360FreqCap(li, fields.freqCap);

  // Flight dates live on the IO's budget segment(s) — the copy template
  // builds exactly one segment per IO and line items inherit
  // (LINE_ITEM_FLIGHT_DATE_TYPE_INHERITED), which is also where the row
  // builder reads start/end from, so this round-trips in the grid. The
  // campaign-level flight (campaignFlight.plannedDates — also always emitted
  // by the copy launcher) is kept in step so the campaign can't state a
  // flight its IO has been edited out of; with several IOs the last edit
  // wins there, which is the copy's intended single-flight semantics.
  applyDv360FlightDates(edited, io, fields);

  // IO-scoped KPI. `kpi` is on the validator's INSERTION ORDER allowlist
  // ({kpiType, kpiAmountMicros, kpiPercentageMicros}) and the copy IO builder
  // always emits one (defaulting to KPI_TYPE_OTHER), so both halves round-trip
  // into the row's kpi / kpiTarget cells. No resolvable IO → no-op.
  if (io && fields.kpi != null) applyDv360IoKpiType(io, fields.kpi);
  if (io && fields.kpiTarget != null) {
    const n = numeric(fields.kpiTarget);
    if (n != null) {
      const kpi = io.kpi || (io.kpi = {});
      kpi.kpiAmountMicros = Math.round(n * 1e6);
    }
  }
}

// ── TTD writers ──────────────────────────────────────────────────────
// Money-shape tolerance (writeTtdMoney) and the pacingLabel inverse
// (ttdPacingModeFromDraft) are imported from sharedFieldWriters.js.

function applyTtdCopyFields(edited, rowId, fields) {
  const ag = asArray(edited.AdGroups)
    .find((x) => x.AdGroupId === rowId || x.adGroupId === rowId);
  const parent = asArray(edited.Campaigns)
    .find((c) => asArray(c.AdGroups).includes(rowId));

  if (ag) {
    if (fields.name != null && String(fields.name).trim()) {
      ag.AdGroupName = String(fields.name).trim();
      // Keep the aliased spelling in sync when a payload carries both.
      if ('adGroupName' in ag) ag.adGroupName = ag.AdGroupName;
    }
    // cpm and bidFloor are the SAME underlying value (ag.BaseBidCPM) surfaced
    // in two columns — both write the key the row builder reads, exactly as
    // CLW's TTD applier does.
    if (fields.cpm != null) writeTtdMoney(ag, 'BaseBidCPM', fields.cpm);
    if (fields.bidFloor != null) writeTtdMoney(ag, 'BaseBidCPM', fields.bidFloor);
    if (fields.bidCeiling != null) writeTtdMoney(ag, 'MaxBidCPM', fields.bidCeiling);
    if (fields.category != null && String(fields.category).trim()) {
      ag.FunnelLocation = String(fields.category).trim();
    }
    // NewFrequencyConfigs — carried forward from the source ad group by the
    // copy builder, so the cell shows a real cap and the shared writer edits
    // the config the formatter surfaces (shared with CLW's applier).
    if (fields.freqCap != null) applyTtdFreqCap(ag, fields.freqCap);
    // ROIGoal — the KIND (kpi) and the target number (kpiTarget) are two
    // cells over the one ROIGoal object the copy carries. Kind first so a
    // target typed in the same batch lands in the new wrapper key.
    if (fields.kpi != null) applyTtdKpiType(ag, fields.kpi);
    if (fields.kpiTarget != null) writeTtdKpiTarget(ag, fields.kpiTarget);
    // NO IsEnabled WRITE. copy_ttd_campaign_launcher builds every copied ad
    // group with `IsEnabled: False` — "copy is created paused/disabled" — and
    // that intent has to survive review: the build is only the FIRST half, and
    // an edit applied afterwards rode IsEnabled straight into POST /adgroup
    // (ttd_adgroup_creator copies the whole config dict) with no creator-side
    // pause to catch it. So one Status cell was the difference between a paused
    // copy and a live, bidding TTD campaign — on a grid that saves instantly.
    // `entityStatus` is denylisted in COPY_TTD_DENYLIST and refused here.
  }

  if (parent) {
    if (fields.market != null && String(fields.market).trim()) {
      parent.CampaignName = String(fields.market).trim();
    }
    if (fields.start != null) {
      const iso = ymdToTtdIso(fields.start);
      if (iso) parent.StartDate = iso;
    }
    if (fields.end != null) {
      const iso = ymdToTtdIso(fields.end);
      if (iso) parent.EndDate = iso;
    }
    // The grid's budget cell shows the campaign budget's PER-AD-GROUP share
    // (buildTtdReviewRows divides by sibling count), so the edit is a share
    // too — scale back up so the value the user typed is exactly what the
    // row shows after the save round-trips. Editing several rows of one
    // campaign in a single save is last-writer-wins by construction.
    if (fields.budget != null) {
      const sibs = asArray(parent.AdGroups).length || 1;
      const total = scaleToParent(numeric(fields.budget), sibs);
      if (total != null) writeTtdMoney(parent, 'Budget', total);
    }
    // Campaign pacing mode ("Ahead" / "Even" / "Off" / "end of flight" —
    // pacingLabel's own vocabulary). The TTD creator drops ad-group-level
    // PacingMode on create, so the campaign is the only slot that matters.
    if (fields.pacing != null) {
      const mode = ttdPacingModeFromDraft(fields.pacing);
      if (mode) parent.PacingMode = mode;
    }
  }
}

// ── Google Ads writers ───────────────────────────────────────────────
// The copy-grid-only field ROUTING; the writers themselves live in
// gads/gadsEdits.js (imported above) so the CLW review grid and this one can't
// drift. bidFloor/businessName/matchType and the ad-group name are delegated
// to gadsEdits.applyGadsEdits (same template shape, already tested there) —
// EXCEPT the PMax cases noted inline where the copy template's spelling
// differs from what that module writes.
//
// applyGadsCampaignFields is called with its DEFAULT options here: this grid's
// budget cell holds the campaign's OWN total (see overlayGadsCopyRows), so no
// share×units scaling, and a target edit leaves the rival target key alone —
// both are the behaviour this grid shipped with.

/**
 * Keep an ALWAYS-ON campaign's budget shape consistent after a grid edit.
 *
 * An always-on copy is built with NO end_date and NO total_budget_micros (see
 * copy_google_ads_campaign_launcher._map_campaign) — search_campaign_validator makes the
 * end date optional but requires the total ALONGSIDE one. This grid's Flight-end cell is
 * editable, and applyGadsCampaignFields writes `end_date` without deriving a total, so
 * typing an end date onto an always-on copy produced a template the validator rejects
 * with "Total budget (total_budget_micros) is missing" — at LAUNCH, two steps later.
 *
 * Deriving it here (daily x inclusive flight days, the same arithmetic
 * CopyGoogleAdsCampaignLauncher._compute_budget does at build time) keeps the pair whole.
 * Only ever ADDS the total — an existing one is the user's own number and is left alone.
 * PMax uses is_lifetime_budget/daily_budget_micros instead and has no such pairing rule,
 * so this is Search-only.
 */
function healAlwaysOnBudget(campaign) {
  if (!campaign || !campaign.end_date || campaign.total_budget_micros) return;
  const daily = campaign.daily_budget_micros;
  if (typeof daily !== 'number' || daily <= 0) return;
  const days = flightDayCount(campaign.start_date, campaign.end_date);
  if (!days) return;
  campaign.total_budget_micros = daily * days;
}

function materializePmaxUtmSuffix(campaign, suffix) {
  const parts = String(suffix || '').trim().replace(/^[?&]+/, '').split('&').filter(Boolean);
  const keyOf = (part) => {
    try { return decodeURIComponent(part.split('=', 1)[0].replace(/\+/g, ' ')).toLowerCase(); }
    catch { return part.split('=', 1)[0].toLowerCase(); }
  };
  const utms = parts.filter((part) => keyOf(part).startsWith('utm_'));
  const remaining = parts.filter((part) => !keyOf(part).startsWith('utm_'));
  if (!utms.length) return remaining.join('&');

  const merge = (url) => {
    if (!url) return url;
    const [withoutFragment, ...fragmentParts] = String(url).split('#');
    const [base, ...queryParts] = withoutFragment.split('?');
    const query = queryParts.join('?').split('&').filter(Boolean);
    const positions = new Map(query.map((part, index) => [keyOf(part), index]));
    utms.forEach((part) => {
      const key = keyOf(part);
      if (positions.has(key)) query[positions.get(key)] = part;
      else {
        positions.set(key, query.length);
        query.push(part);
      }
    });
    return `${base}${query.length ? `?${query.join('&')}` : ''}`
      + `${fragmentParts.length ? `#${fragmentParts.join('#')}` : ''}`;
  };

  campaign.final_url = merge(campaign.final_url);
  asArray(campaign.asset_groups).forEach((group) => {
    if (group?.final_url) group.final_url = merge(group.final_url);
  });
  asArray(campaign.sitelinks).forEach((sitelink) => {
    if (sitelink?.final_url && !sitelink.final_url_suffix) {
      sitelink.final_url = merge(sitelink.final_url);
    }
  });
  return remaining.join('&');
}

function applyGadsCopyFields(edited, subtype, rowId, fields) {
  const at = parseGadsRowId(rowId);
  if (!at) return;
  const campaign = asArray(edited.campaigns)[at.campaignIdx];
  if (!campaign) return;

  if (fields.market != null && String(fields.market).trim()) {
    campaign.name = String(fields.market).trim();
  }
  // The grid's sticky first column is the CAMPAIGN name (see
  // overlayGadsCopyRows), so an edit there renames the campaign — every row of
  // that campaign re-reads it. (CLW's grid spells that column `market` and
  // uses `name` for the ad group, which is why this stays grid-local.)
  if (fields.name != null && String(fields.name).trim()) {
    campaign.name = String(fields.name).trim();
  }

  // CAMPAIGN URL TRACKING. An EMPTY string is meaningful here and must not be
  // treated as "no edit": clearing the box is the only way to say "this copy has
  // no tracking template", which is exactly the fix for a source template that
  // collides with its own sitelink URLs. So the key is DELETED rather than set
  // to "" — the creators read `if campaign_config.get('tracking_url_template')`,
  // and an empty string would also make the review panel render a blank row
  // instead of omitting it.
  if (fields.trackingUrlTemplate != null) {
    const v = String(fields.trackingUrlTemplate).trim();
    if (v) campaign.tracking_url_template = v;
    else delete campaign.tracking_url_template;
  }
  if (fields.finalUrlSuffix != null) {
    // Query TEXT, not a query string: a leading ?/& would land inside the first
    // parameter's name. Same hygiene the step-1 box used to apply.
    const v = String(fields.finalUrlSuffix).trim().replace(/^[?&]+/, '');
    const suffix = subtype === 'pmax' ? materializePmaxUtmSuffix(campaign, v) : v;
    if (suffix) campaign.final_url_suffix = suffix;
    else delete campaign.final_url_suffix;
  }

  if (subtype === 'search') {
    const adGroups = asArray(campaign.ad_groups);
    const ag = adGroups.length ? adGroups[at.unitIdx] : null;
    if (adGroups.length && !ag) return;
    const target = ag || campaign; // no ad_groups → the row IS the campaign
    applyGadsCampaignFields(campaign, fields);
    healAlwaysOnBudget(campaign);
    if (fields.clickURL != null && String(fields.clickURL).trim()) {
      target.click_url = String(fields.clickURL).trim();
    }
    if (fields.headlines != null) writeGadsList(target, 'headline_texts', 'headlines', fields.headlines);
    if (fields.descriptions != null) writeGadsList(target, 'description_texts', 'descriptions', fields.descriptions);
    if (fields.keywords != null) rebuildGadsKeywords(target, fields.keywords);
    if (fields.displayPath != null) applyGadsDisplayPath(target, fields.displayPath);
    return;
  }

  // PMax — asset groups are inline on the campaign in the copy template.
  const group = asArray(campaign.asset_groups)[at.unitIdx];
  applyGadsCampaignFields(campaign, fields);
  if (fields.clickURL != null && String(fields.clickURL).trim()) {
    // The creator's URL is campaign-level final_url (seeded from the first
    // asset group at build time); keep the asset group's own copy in sync
    // when it carries one so the two never contradict.
    campaign.final_url = String(fields.clickURL).trim();
    if (group && group.final_url) group.final_url = campaign.final_url;
  }
  if (!group) return;
  if (fields.adGroupName != null && String(fields.adGroupName).trim()) {
    group.name = String(fields.adGroupName).trim();
  }
  // The copy template stores long headlines as a LIST (`long_headlines`);
  // gadsEdits writes the singular `long_headline`, which the PMax creator
  // ignores — so the list's first entry is written here instead.
  if (fields.longHeadline != null) {
    if (Array.isArray(group.long_headlines) && group.long_headlines.length) {
      group.long_headlines[0] = String(fields.longHeadline);
    } else {
      group.long_headlines = [String(fields.longHeadline)];
    }
  }
  if (fields.headlines != null) writeGadsList(group, 'headlines', 'headline_texts', fields.headlines);
  if (fields.descriptions != null) writeGadsList(group, 'descriptions', 'description_texts', fields.descriptions);
}

// Fields handled by this module's own gads writer — everything else falls
// through to gadsEdits.applyGadsEdits.
const GADS_COPY_OWN_FIELDS = new Set([
  'market', 'clickURL', 'headlines', 'descriptions', 'longHeadline',
  'start', 'end', 'dailyBudget', 'budget', 'targetLabel', 'keywords', 'displayPath',
  // Campaign-level URL tracking, edited in step 2's URL panel. Ours rather than
  // gadsEdits' because the copy templates carry these at the campaign and
  // gadsEdits has no writer for them at all.
  'trackingUrlTemplate', 'finalUrlSuffix',
  // `name` is the CAMPAIGN name in this grid (sticky first column) — always
  // ours; gadsEdits' own `name` writer targets the search AD GROUP, which is
  // now the separate `adGroupName` column.
  'name',
]);
// PMax 'adGroupName' targets the asset group's own `name` key (gadsEdits has
// no asset-group name writer at all), so it's ours on that subtype; on search
// it is delegated to gadsEdits, renamed back to `name` at the call site.
const isGadsOwnField = (subtype, field) => GADS_COPY_OWN_FIELDS.has(field)
  || (subtype !== 'search' && field === 'adGroupName');

/**
 * Overlay the copy grid's scalar edits + list operations onto a deep clone of
 * launcherResult. Never mutates its inputs; the clone carries targeting_plan /
 * creative_plan / gates through (targeting_plan possibly PRUNED by a removal —
 * that IS the edit), so the caller can PUT the return value whole (see
 * savePlanningReview's contract).
 *
 * `edits` is {rowId: {field: value}}. `listOpsOrDeletions` accepts BOTH shapes
 * the edit hook has produced over time — the same contract
 * CampaignLaunchWorkflow/reviewEdits.js#applyLauncherResultEdits states:
 *   - list ops (current): { rowId: { field: [{ op: 'remove'|'add'|'edit', value, newValue? }] } }
 *   - legacy deletions:   { rowId: { field: [label] } }  (remove-only)
 * Legacy string arrays normalize to remove ops internally (shared
 * normalizeRowOps), so removes behave byte-identically to the legacy path.
 *
 * What each family can honour:
 *   - Google Ads — the full op set, delegated to gads/gadsEdits.js (keywords,
 *     negatives, headlines/descriptions, sitelinks, zip codes, geo, search
 *     themes, audiences, images/videos): the copy templates are exactly the
 *     shape that module writes.
 *   - DV360 — REMOVE only, against the targeting_plan sidecar (adds need
 *     option-ID lookups; see applyDv360TargetingDeletions).
 *   - TTD — none: every list cell on a TTD copy (geo, deals, audiences) is
 *     carried from the source's bid lists BY REFERENCE, so there is nothing in
 *     the template to write. Ops are a no-op and the grid re-renders unchanged.
 *
 * `reviewRows` (optional) restricts writes to rowIds the current grid knows.
 */
export function applyCopyReviewEdits(launcherResult, edits, listOpsOrDeletions = {}, reviewRows = [], rowOps = []) {
  const edited = JSON.parse(JSON.stringify(launcherResult || {}));
  const family = copyResultFamily(edited);
  if (!family) return edited;
  const known = reviewRows.length ? new Set(reviewRows.map((r) => r.rowId)) : null;
  const subtype = family === 'gads' ? gadsResultSubtype(edited) : null;

  const gadsDelegated = {};
  for (const [rowId, fields] of Object.entries(edits || {})) {
    if (known && !known.has(rowId)) continue;
    if (family === 'ttd') {
      applyTtdCopyFields(edited, rowId, fields);
    } else if (family === 'dv360') {
      applyDv360CopyFields(edited, rowId, fields);
    } else {
      const own = {};
      const rest = {};
      for (const [field, value] of Object.entries(fields || {})) {
        if (isGadsOwnField(subtype, field)) own[field] = value;
        else rest[field] = value;
      }
      if (Object.keys(own).length) applyGadsCopyFields(edited, subtype, rowId, own);
      if (Object.keys(rest).length) {
        // gadsEdits speaks `name` for the search AD GROUP; this grid calls
        // that column `adGroupName` (its `name` is the campaign, handled
        // above), so translate on the way out.
        if (rest.adGroupName != null) {
          rest.name = rest.adGroupName;
          delete rest.adGroupName;
        }
        gadsDelegated[rowId] = rest;
      }
    }
  }

  if (family === 'gads') {
    // name (search ad group) / bidFloor / businessName + every list op — the
    // copy template is exactly the shape gadsEdits was written against, and
    // that module normalizes legacy label arrays itself, so both shapes pass
    // through untouched.
    applyGadsEdits(edited, gadsDelegated, listOpsOrDeletions, reviewRows, rowOps);
  }
  if (family === 'ttd') {
    for (const [rowId, fields] of Object.entries(listOpsOrDeletions || {})) {
      if (known && !known.has(rowId)) continue;
      for (const [subField, ops] of Object.entries(fields || {})) {
        if (subField === 'geoInclude' || subField === 'geoExclude') {
          applyTtdGeoOps(edited, rowId, subField, ops);
        }
      }
    }
  }
  if (family === 'dv360') {
    for (const [rowId, fields] of Object.entries(listOpsOrDeletions || {})) {
      if (known && !known.has(rowId)) continue;
      applyDv360TargetingDeletions(edited, rowId, normalizeRowOps(fields), reviewRows);
    }
  }

  // Row-level ops run LAST, after every value edit has resolved against the
  // rows that existed when the batch was staged — a delete renumbers the rest.
  // Google Ads is already handled inside applyGadsEdits above (it owns the
  // positional addressing for that family), so only TTD and DV360 need a call
  // here. Clone overrides go through this module's own copy field writers, so a
  // cloned row is written by the same code path a typed edit uses.
  if (Array.isArray(rowOps) && rowOps.length && family !== 'gads') {
    const { created } = family === 'ttd'
      ? applyTtdRowOps(edited, rowOps)
      : applyDv360RowOps(edited, rowOps);
    for (const { rowId, overrides } of created) {
      if (!overrides || !Object.keys(overrides).length) continue;
      if (family === 'ttd') applyTtdCopyFields(edited, rowId, overrides);
      else applyDv360CopyFields(edited, rowId, overrides);
    }
  }
  return edited;
}

/**
 * Rename the campaign(s) a copy will create — the "use a unique name"
 * quick-fix's write path. DV360 grids have no campaign-name row/column (the
 * template's one campaign sits above the line items), so the fix writes the
 * launcherResult directly rather than synthesizing a grid edit.
 *
 * `matchName` limits the rename to campaigns currently carrying that exact
 * name (the gate row's flagged `value`); omitted, every campaign in the
 * template is renamed — correct for the single-campaign templates the copy
 * launchers emit. Returns a NEW deep-cloned launcherResult.
 */
export function renameCampaignInLauncherResult(launcherResult, newName, matchName = null) {
  const edited = JSON.parse(JSON.stringify(launcherResult || {}));
  const family = copyResultFamily(edited);
  const name = String(newName || '').trim();
  if (!family || !name) return edited;
  const matches = (current) => matchName == null || current === matchName;

  if (family === 'dv360') {
    if (edited.campaign && matches(edited.campaign.displayName)) {
      edited.campaign.displayName = name;
    }
  } else if (family === 'ttd') {
    for (const c of asArray(edited.Campaigns)) {
      if (matches(c.CampaignName)) c.CampaignName = name;
    }
  } else {
    for (const c of asArray(edited.campaigns)) {
      if (matches(c.name)) c.name = name;
    }
  }
  return edited;
}

// ── ROW REMOVAL (excluding a child from the copy) ────────────────────
//
// The 3-step flow deleted the "Confirm what's copied" picker step on the
// stated grounds that "excluding children is a row DELETION in the Adjust
// grid — it edits the REAL built template through the same planning-review
// PUT as every other edit, strictly better than pre-build id exclusion"
// (commit b3421b00). That deletion was never implemented, so until now there
// was no way to drop a child from a copy at all: not pre-build (the excluded
// ids plumbing went with the picker) and not in the grid.
//
// This is that operation. It mutates the built template, so it rides the
// existing planning-review PUT and needs no new endpoint.
//
// Refusing the LAST child is deliberate: a campaign / insertion order with no
// children cannot launch, and silently producing one would fail at push with a
// platform error instead of here with an explanation. Callers surface `reason`.

/**
 * Drop the per-line-item sidecars for line items leaving a DV360 copy.
 *
 * Both are keyed by lineItemId, and a stale entry is not inert: the launcher
 * applies targeting_plan after create and resolves geo_plan names at launch,
 * so an entry naming an entity that no longer exists is work queued against
 * nothing. Mutates `edited`.
 *
 * An emptied bag is left in place rather than deleted: both readers treat a
 * missing key and an empty one the same, and removing the key would change
 * what callers of removeCopyRow have always been handed back.
 */
function dropDv360LineItemSidecars(edited, rowIds) {
  for (const key of ['targeting_plan', 'geo_plan']) {
    const bag = edited[key];
    if (!bag || typeof bag !== 'object') continue;
    for (const rowId of rowIds) delete bag[String(rowId)];
  }
}

/** Which list a Google Ads campaign keeps its grid rows in. */
function gadsUnitListKey(campaign) {
  if (Array.isArray(campaign?.asset_groups)) return 'asset_groups';
  if (Array.isArray(campaign?.ad_groups)) return 'ad_groups';
  return null;
}

/**
 * Remove one child row from a built copy template.
 *
 * @param {Object} launcherResult the built template (not mutated)
 * @param {string} rowId          the grid row's id
 * @returns {{launcherResult: Object, removed: boolean, reason: string|null}}
 *          `launcherResult` is the ORIGINAL object when nothing was removed,
 *          so callers can skip the save by identity.
 */
export function removeCopyRow(launcherResult, rowId) {
  const family = copyResultFamily(launcherResult);
  if (!family || !rowId) {
    return { launcherResult, removed: false, reason: 'Nothing to remove' };
  }
  const edited = JSON.parse(JSON.stringify(launcherResult));

  if (family === 'dv360') {
    const items = asArray(edited.lineItems);
    const idx = items.findIndex((x) => x.lineItemId === rowId);
    if (idx < 0) return { launcherResult, removed: false, reason: 'Line item not found in the copy' };
    const ios = asArray(edited.insertionOrders);
    const ioIdx = ios.findIndex((x) => asArray(x.lineItems).includes(rowId));
    const io = ioIdx >= 0 ? ios[ioIdx] : null;
    // The LAST line item takes its INSERTION ORDER with it.
    //
    // This used to be a refusal — "an insertion order with no line items cannot
    // launch" — which was true about the state it was avoiding and useless as
    // an answer: it left no way to drop an order at all, so the user was told
    // the reason their removal was impossible instead of being given it. The
    // premise is what changed, not the constraint: an emptied order is not
    // created, so nothing needs it once its only child is gone.
    const dropsIo = io != null && asArray(io.lineItems).length <= 1;
    if (dropsIo && ios.length <= 1) {
      // One level up the same wall stands, and there is nothing above the
      // campaign to remove instead.
      return {
        launcherResult,
        removed: false,
        reason: 'This is the last line item in the copy’s only insertion order — a campaign '
          + 'with nothing under it cannot launch',
      };
    }
    items.splice(idx, 1);
    if (dropsIo) ios.splice(ioIdx, 1);
    else if (io) io.lineItems = asArray(io.lineItems).filter((id) => id !== rowId);
    dropDv360LineItemSidecars(edited, [rowId]);
    return { launcherResult: edited, removed: true, reason: null };
  }

  if (family === 'ttd') {
    const groups = asArray(edited.AdGroups);
    const idx = groups.findIndex((x) => x.AdGroupId === rowId || x.adGroupId === rowId);
    if (idx < 0) return { launcherResult, removed: false, reason: 'Ad group not found in the copy' };
    const parent = asArray(edited.Campaigns)
      .find((c) => asArray(c.AdGroups).includes(rowId));
    if (parent && asArray(parent.AdGroups).length <= 1) {
      return {
        launcherResult,
        removed: false,
        reason: 'This is the only ad group in its campaign — a campaign with no ad groups cannot launch',
      };
    }
    groups.splice(idx, 1);
    if (parent) parent.AdGroups = asArray(parent.AdGroups).filter((id) => id !== rowId);
    return { launcherResult: edited, removed: true, reason: null };
  }

  // Google Ads (Search / PMax). rowIds are POSITIONAL (`gads-{ci}-{ui}`), so
  // the surviving rows are re-derived from the spliced template by the caller's
  // rebuild — no id fixup is needed here, but a held rowId (e.g. the open cell
  // panel's) can go stale, which is why the caller clears its selection.
  const at = parseGadsRowId(rowId);
  const campaign = at ? asArray(edited.campaigns)[at.campaignIdx] : null;
  if (!campaign) return { launcherResult, removed: false, reason: 'Campaign not found in the copy' };
  const listKey = gadsUnitListKey(campaign);
  const unitNoun = listKey === 'asset_groups' ? 'asset group' : 'ad group';
  if (!listKey || !campaign[listKey][at.unitIdx]) {
    return { launcherResult, removed: false, reason: `${unitNoun} not found in the copy` };
  }
  if (campaign[listKey].length <= 1) {
    return {
      launcherResult,
      removed: false,
      reason: `This is the only ${unitNoun} in its campaign — a campaign with none cannot launch`,
    };
  }
  campaign[listKey].splice(at.unitIdx, 1);
  return { launcherResult: edited, removed: true, reason: null };
}

/**
 * Remove SEVERAL rows in one edit (the grid's "Remove selected").
 *
 * The removals themselves are just removeCopyRow folded over the ids — that
 * already drops a DV360 insertion order along with its last line item, so a
 * selection covering a whole order empties the order and then takes it.
 *
 * What this adds is the ONE guard that cannot be made per row: a selection
 * covering EVERY insertion order in the copy. Folded row by row that removes
 * all but the last order and then refuses, leaving a campaign the user asked
 * to empty in a shape they did not ask for. It is declined whole instead.
 *
 * @param {Object} launcherResult the built template (not mutated)
 * @param {string[]} rowIds       the grid rows to drop
 * @returns {{launcherResult: Object, removedCount: number, reasons: string[]}}
 *          `launcherResult` is the ORIGINAL object when nothing was removed.
 */
export function removeCopyRows(launcherResult, rowIds) {
  const ids = asArray(rowIds).map(String).filter(Boolean);
  if (ids.length === 0) {
    return { launcherResult, removedCount: 0, reasons: ['Nothing to remove'] };
  }

  if (copyResultFamily(launcherResult) === 'dv360') {
    const idSet = new Set(ids);
    const ios = asArray(launcherResult.insertionOrders);
    // An IO the selection covers completely — every one of its line-item refs
    // is checked — is an IO this removal would take with it.
    const doomed = ios.filter((io) => {
      const refs = asArray(io.lineItems).map(String);
      return refs.length > 0 && refs.every((r) => idSet.has(r));
    });
    if (ios.length > 0 && doomed.length >= ios.length) {
      return {
        launcherResult,
        removedCount: 0,
        reasons: ['That selection is every insertion order in the copy — a campaign with no '
          + 'insertion orders cannot launch. Leave at least one line item selected out.'],
      };
    }
  }

  // A refusal skips its row and keeps the rest: a bulk action that aborts
  // wholesale on one bad row makes the user hunt for which one it was.
  let next = launcherResult;
  let removedCount = 0;
  const reasons = [];
  for (const id of ids) {
    const step = removeCopyRow(next, id);
    if (step.removed) { next = step.launcherResult; removedCount += 1; }
    else if (step.reason && !reasons.includes(step.reason)) reasons.push(step.reason);
  }
  return { launcherResult: next, removedCount, reasons };
}

// ── TTD GEO (taking ownership of a referenced bid list) ──────────────
//
// A TTD copy carries the source's geo BY REFERENCE: the ad group's
// AssociatedBidLists point at the source's geo bid lists, and the template holds
// no geo of its own — which is why every geo cell was read-only ("edit the
// source, or the copy after launch").
//
// Those lists are owned by the SOURCE AD GROUP, not the advertiser — assuming
// otherwise is what made TTD reject entire copies with "the ad group is not
// authorized to access one or more bid lists". The backend now drops any
// reference it cannot prove is library-available and restates the geo instead.
//
// It does not have to stay that way. The TTD launcher already accepts a
// per-ad-group `Geography` directive and builds the bid list itself:
//
//   ttd_adgroup_creator.py:415-423   Geography (a dict) -> adgroup_id_to_geo,
//                                    popped BEFORE the create call, so it is a
//                                    launcher directive, not a TTD API field
//   ttd_campaign_launcher.py:1264-1270  -> create_geo_bidlists(...) -> appended
//                                    to associated_bidlists -> ad group update
//
// The list it creates is ad-group-owned (`IsAvailableForLibraryUse: false`), so
// editing here gives the COPY its own geo list and never mutates the shared one
// the source still uses.
//
// So the first geo edit on a row takes OWNERSHIP: it states the full desired
// segment-id set on `Geography` and drops the source's geo-only bid lists from
// AssociatedBidLists. Both halves are required — leaving the reference attached
// would target the source's markets AND the new list. The starting set comes
// from review_enrichment's geo_include_ids / geo_exclude_ids and the bid lists
// to drop from geo_bidlist_ids (all three added in PR #2370); geo_bidlist_ids
// deliberately lists only geo-ONLY bid lists, so this can never drop a list
// that also carries PMP deals.
//
// The grid's {id,label} pairs ride review_enrichment, the copy's review-only
// sidecar, because the cell has to re-render labels across edits and an unknown
// key left on the ad group BODY would be POSTed to TTD. `Geography` itself is
// not that risk — the creator pops it before POSTing — so it carries the
// launcher's canonical segment dicts, ids and names together.

const TTD_GEO_BUCKET = { geoInclude: 'TargetGeoSegmentIds', geoExclude: 'BlockGeoSegmentIds' };

/** `id|label` from the panel's lookup pick, or a bare id/label. */
/**
 * Decode a lookup-backed list value into { id, label }.
 *
 * This used to split on '|', but CellDetailPanel stages a picked lookup row with
 * encodeLookupValue(), which joins on LOOKUP_VALUE_SEP (''). So the pipe
 * never appeared, indexOf returned -1, and the value came back as
 * { id: <whole string>, label: <whole string> } — which applyTtdGeoOps reads as
 * "no real id" and SKIPS. Every geo add on a TTD copy was silently dropped: the
 * chip appeared in the panel, the save ran, and the template was unchanged.
 *
 * Delegating to the shared decoder is what keeps the two ends of the same wire
 * format in agreement; CLW's applyTtdListOps has always used it.
 */
function splitLookupValue(raw) {
  // Prefer the shared separator — that is what CellDetailPanel actually stages.
  const decoded = decodeLookupValue(raw);
  const id = String(decoded.id ?? '').trim();
  const label = String(decoded.label ?? '').trim();
  if (id && label && id !== label) return { id, label };

  // No shared separator present: fall back to the pipe form. Kept because the
  // gate quick-fixes and this module's own tests state values that way, and a
  // tolerant decode costs nothing while a strict one would break them.
  const str = String(raw ?? '').trim();
  const bar = str.indexOf('|');
  return bar > 0
    ? { id: str.slice(0, bar).trim(), label: str.slice(bar + 1).trim() }
    : { id: str, label: str };
}

/** The row's geo as [{id, label}], from a prior edit or the source enrichment. */
function ttdGeoEntries(edited, rowId, subField) {
  const enrich = (edited.review_enrichment || {})[rowId] || {};
  const editedKey = subField === 'geoExclude' ? 'geo_exclude_edited' : 'geo_include_edited';
  if (Array.isArray(enrich[editedKey])) return enrich[editedKey].map((e) => ({ ...e }));
  const idsKey = subField === 'geoExclude' ? 'geo_exclude_ids' : 'geo_include_ids';
  const namesKey = subField === 'geoExclude' ? 'geo_exclude' : 'geo_include';
  const ids = asArray(enrich[idsKey]);
  const names = asArray(enrich[namesKey]);
  // Names and ids are recorded in the same order, but an id whose name did not
  // reverse-map has no entry in names — fall back to the id so it is still
  // visible and still launches.
  return ids.map((id, i) => ({ id: String(id), label: String(names[i] ?? id) }));
}

function applyTtdGeoOps(edited, rowId, subField, ops) {
  const bucket = TTD_GEO_BUCKET[subField];
  if (!bucket) return;
  const ag = asArray(edited.AdGroups)
    .find((x) => x.AdGroupId === rowId || x.adGroupId === rowId);
  if (!ag) return;

  let entries = ttdGeoEntries(edited, rowId, subField);
  let touched = false;
  for (const op of asArray(ops)) {
    if (op.op === 'add') {
      const { id, label } = splitLookupValue(op.value);
      if (!label) continue;
      // A typed market name has no GeoSegmentId, and does not need one: the
      // launcher resolves Name → GeoSegmentId when it builds the bid list. An
      // id-less entry is recorded with id: null so the dedupe below matches on
      // the label instead.
      const resolvedId = id && id !== label ? id : null;
      if (entries.some((e) => (resolvedId && e.id === resolvedId) || e.label === label)) continue;
      entries.push({ id: resolvedId, label });
      touched = true;
    } else if (op.op === 'edit') {
      const from = splitLookupValue(op.value).label;
      const { id: rawId, label: to } = splitLookupValue(op.newValue);
      if (!to) continue;
      const hit = entries.find((e) => e.label === from);
      if (!hit) continue;
      // Renaming to a different market invalidates an id resolved for the old
      // one — drop it and let the launcher re-resolve from the new name.
      hit.label = to;
      hit.id = rawId && rawId !== to ? rawId : null;
      touched = true;
    } else if (op.op === 'remove') {
      const { id, label } = splitLookupValue(op.value);
      const next = entries.filter((e) => (id ? e.id !== id : true) && e.label !== label);
      if (next.length !== entries.length) { entries = next; touched = true; }
    }
  }
  if (!touched) return;

  // Take ownership: state the full set, and stop referencing the source's
  // geo-only bid lists so the two don't both apply.
  const enrich = edited.review_enrichment || (edited.review_enrichment = {});
  const rowEnrich = enrich[rowId] || (enrich[rowId] = {});
  rowEnrich[subField === 'geoExclude' ? 'geo_exclude_edited' : 'geo_include_edited'] = entries;

  const geoBidListIds = new Set(asArray(rowEnrich.geo_bidlist_ids).map(String));
  if (geoBidListIds.size) {
    ag.AssociatedBidLists = asArray(ag.AssociatedBidLists)
      .filter((a) => !geoBidListIds.has(String((a || {}).BidListId || '')));
  }

  const geography = ag.Geography && typeof ag.Geography === 'object' ? ag.Geography : {};
  // One dict per segment, keyed GeoSegmentId — matching CLW's reviewEdits and
  // the launcher, which reads each segment with .get("GeoSegmentId"). A bare id
  // array crashed the bid list worker ("'str' object has no attribute 'get'").
  // Name rides inside the segment rather than the sidecar: the creator pops the
  // whole Geography key before POSTing, so nothing in here reaches TTD.
  // An id-less entry emits {Name} only. Never `GeoSegmentId: null` — the bid list
  // creator treats a falsy id as "resolve this by Name", and a literal null key
  // would read as a segment that was already resolved to nothing.
  const segs = entries.map((e) => (e.id ? { GeoSegmentId: e.id, Name: e.label } : { Name: e.label }));
  if (segs.length) geography[bucket] = { GeoSegmentIds: segs };
  else delete geography[bucket];
  if (Object.keys(geography).length) ag.Geography = geography;
  else delete ag.Geography;
}
